/**
 * BookingsTimeFilter — full-featured date & time-slot filter for the Bookings table.
 *
 * Filter modes:
 *  - Quick presets: All · Today · Tomorrow · This Week · This Month · Next 7 Days · Past 30 Days
 *  - Custom date range: From / To inclusive (date-only)
 *  - Time slot:        morning / afternoon / evening or any of the 6 preset HH:MM windows
 *  - Search box:       free-text over booking_code · customer_name · phone
 *
 * Exposes one prop:
 *  - onChange({ scope, from, to, slotKey, search })
 *
 * `applyFilter(bookings, filter)` is a pure helper that callers use to slice the
 * full list. Keeping presentation & logic together so the AdminPortal can stay slim.
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Filter, X, Calendar, Clock, Search } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';

const PRESETS = [
  { key: 'all',        label: 'All time'      },
  { key: 'today',      label: 'Today'         },
  { key: 'tomorrow',   label: 'Tomorrow'      },
  { key: 'this_week',  label: 'This Week'     },
  { key: 'this_month', label: 'This Month'    },
  { key: 'next_7',     label: 'Next 7 Days'   },
  { key: 'past_30',    label: 'Past 30 Days'  },
  { key: 'custom',     label: 'Custom Range…' },
];

const TIME_SLOTS = [
  { key: 'any',       label: 'Any time'         },
  { key: 'morning',   label: 'Morning (8 AM – 12 PM)' },
  { key: 'afternoon', label: 'Afternoon (12 – 4 PM)'  },
  { key: 'evening',   label: 'Evening (4 – 8 PM)'     },
  { key: '08-10',     label: '8 – 10 AM'  },
  { key: '10-12',     label: '10 AM – 12 PM' },
  { key: '12-14',     label: '12 – 2 PM'  },
  { key: '14-16',     label: '2 – 4 PM'   },
  { key: '16-18',     label: '4 – 6 PM'   },
  { key: '18-20',     label: '6 – 8 PM'   },
];

const todayStr = () => new Date().toISOString().slice(0, 10);

function shiftDate(d, days) {
  const c = new Date(d);
  c.setDate(c.getDate() + days);
  return c.toISOString().slice(0, 10);
}

function startOfWeek(d) {
  const c = new Date(d);
  const day = c.getDay();              // 0 = Sun … 6 = Sat
  const diff = (day === 0 ? -6 : 1 - day); // Monday as start of week
  c.setDate(c.getDate() + diff);
  return c.toISOString().slice(0, 10);
}

function endOfWeek(d) {
  return shiftDate(startOfWeek(d), 6);
}

function startOfMonth(d) {
  const c = new Date(d);
  c.setDate(1);
  return c.toISOString().slice(0, 10);
}

function endOfMonth(d) {
  const c = new Date(d);
  c.setMonth(c.getMonth() + 1);
  c.setDate(0);
  return c.toISOString().slice(0, 10);
}

export function resolvePresetRange(scope) {
  const t = todayStr();
  switch (scope) {
    case 'today':      return { from: t, to: t };
    case 'tomorrow':   return { from: shiftDate(t, 1), to: shiftDate(t, 1) };
    case 'this_week':  return { from: startOfWeek(t), to: endOfWeek(t) };
    case 'this_month': return { from: startOfMonth(t), to: endOfMonth(t) };
    case 'next_7':     return { from: t, to: shiftDate(t, 7) };
    case 'past_30':    return { from: shiftDate(t, -30), to: t };
    default:           return { from: null, to: null };
  }
}

function slotMatches(booking, slotKey) {
  if (!slotKey || slotKey === 'any') return true;
  const start = booking.time_slot_start;            // HH:MM
  const end = booking.time_slot_end;
  // Period buckets
  if (slotKey === 'morning' || slotKey === 'afternoon' || slotKey === 'evening') {
    if (!start) {
      // Fall back to the legacy `scheduled_time_slot` string
      const legacy = String(booking.scheduled_time_slot || '').toLowerCase();
      return legacy.includes(slotKey);
    }
    const h = parseInt(start.slice(0, 2), 10);
    if (slotKey === 'morning')   return h >= 6  && h < 12;
    if (slotKey === 'afternoon') return h >= 12 && h < 16;
    if (slotKey === 'evening')   return h >= 16 && h < 22;
  }
  // Exact preset windows (e.g. "08-10" → 08:00-10:00)
  if (/^\d{2}-\d{2}$/.test(slotKey)) {
    const [sH, eH] = slotKey.split('-');
    return start === `${sH}:00` && end === `${eH}:00`;
  }
  return true;
}

export function applyTimeFilter(bookings, filter, opts = {}) {
  if (!filter) return bookings;
  const { from, to, slotKey, search } = filter;
  const dateField = opts.dateField || 'scheduled_date';
  const fallbackField = opts.fallbackDateField || 'created_at';
  const searchFields = opts.searchFields || [
    'booking_code', 'customer_name', 'customer_phone', 'service_label', 'customer_city',
  ];
  const ignoreSlot = opts.ignoreSlot === true;
  const q = (search || '').trim().toLowerCase();
  return (bookings || []).filter((b) => {
    if (from || to) {
      const d = b[dateField] || (b[fallbackField] || '').slice(0, 10);
      if (!d) return false;
      if (from && d < from) return false;
      if (to && d > to) return false;
    }
    if (!ignoreSlot && !slotMatches(b, slotKey)) return false;
    if (q) {
      const hay = searchFields.map((k) => b[k]).filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

export default function BookingsTimeFilter({ value, onChange, hideSlot = false, label = 'Date & Time', searchPlaceholder = 'BK260622001, name, phone, city…' }) {
  const [scope, setScope]   = useState(value?.scope || 'all');
  const [from, setFrom]     = useState(value?.from || '');
  const [to, setTo]         = useState(value?.to || '');
  const [slotKey, setSlot]  = useState(value?.slotKey || 'any');
  const [search, setSearch] = useState(value?.search || '');
  const [open, setOpen]     = useState(false);

  // Derive from/to whenever the preset scope changes (except custom)
  useEffect(() => {
    if (scope === 'custom' || scope === 'all') return;
    const r = resolvePresetRange(scope);
    setFrom(r.from || '');
    setTo(r.to || '');
  }, [scope]);

  // Bubble changes up — keep onChange in a ref so an unstable parent callback
  // (e.g. inline arrow recreated each render) does NOT retrigger this effect
  // and cause an infinite setFilters → re-render loop in the consumer.
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  useEffect(() => {
    onChangeRef.current?.({
      scope,
      from: scope === 'all' ? null : from || null,
      to:   scope === 'all' ? null : to || null,
      slotKey,
      search,
    });
  }, [scope, from, to, slotKey, search]);

  const summary = useMemo(() => {
    const bits = [];
    const p = PRESETS.find((x) => x.key === scope);
    if (p && scope !== 'all') bits.push(p.label);
    if (scope === 'custom' && (from || to)) bits.push(`${from || '…'} → ${to || '…'}`);
    if (!hideSlot && slotKey !== 'any') {
      const s = TIME_SLOTS.find((x) => x.key === slotKey);
      if (s) bits.push(s.label);
    }
    if (search.trim()) bits.push(`"${search.trim()}"`);
    return bits.join(' · ');
  }, [scope, from, to, slotKey, search]);

  const reset = () => {
    setScope('all');
    setFrom(''); setTo('');
    setSlot('any');
    setSearch('');
  };

  const activeCount = (scope !== 'all' ? 1 : 0) + (!hideSlot && slotKey !== 'any' ? 1 : 0) + (search.trim() ? 1 : 0);

  return (
    <div className="relative" data-testid="bookings-time-filter">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`h-10 px-3 rounded-lg text-sm font-medium border inline-flex items-center gap-2 transition ${
          activeCount > 0
            ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
            : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
        }`}
        data-testid="bookings-time-filter-toggle"
      >
        <Filter size={14} />
        <span>{label}</span>
        {activeCount > 0 && (
          <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-emerald-600 text-white text-[10px] font-bold" data-testid="bookings-time-filter-badge">
            {activeCount}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 z-40 w-[420px] max-w-[92vw] bg-white border border-slate-200 rounded-2xl shadow-2xl p-4 space-y-4" data-testid="bookings-time-filter-popover">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar size={15} className="text-emerald-700" />
                <h4 className="font-semibold text-slate-900 text-sm">{label} filter</h4>
              </div>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
            </div>

            {/* Quick presets */}
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Date range</Label>
              <div className="grid grid-cols-4 gap-1.5 mt-1.5" data-testid="bookings-time-filter-presets">
                {PRESETS.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => setScope(p.key)}
                    className={`text-xs px-2 py-1.5 rounded-md border transition truncate ${
                      scope === p.key
                        ? 'bg-emerald-600 border-emerald-600 text-white shadow-sm'
                        : 'bg-white border-slate-200 text-slate-700 hover:border-emerald-300'
                    }`}
                    data-testid={`preset-${p.key}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom range — visible when preset is custom or both from/to set */}
            {(scope === 'custom' || scope !== 'all') && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-slate-500">From</Label>
                  <Input
                    type="date"
                    value={from}
                    onChange={(e) => { setFrom(e.target.value); setScope('custom'); }}
                    className="h-9 mt-1"
                    data-testid="bookings-time-filter-from"
                  />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-slate-500">To</Label>
                  <Input
                    type="date"
                    value={to}
                    onChange={(e) => { setTo(e.target.value); setScope('custom'); }}
                    className="h-9 mt-1"
                    data-testid="bookings-time-filter-to"
                  />
                </div>
              </div>
            )}

            {/* Time slot */}
            {!hideSlot && (
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                  <Clock size={11} /> Time slot
                </Label>
                <select
                  value={slotKey}
                  onChange={(e) => setSlot(e.target.value)}
                  className="mt-1.5 w-full h-9 px-3 rounded-md border border-slate-300 bg-white text-sm"
                  data-testid="bookings-time-filter-slot"
                >
                  {TIME_SLOTS.map((s) => (
                    <option key={s.key} value={s.key}>{s.label}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Free-text search */}
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                <Search size={11} /> Search
              </Label>
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={searchPlaceholder}
                className="h-9 mt-1.5"
                data-testid="bookings-time-filter-search"
              />
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-slate-100">
              <div className="text-[11px] text-slate-500 truncate flex-1 mr-2" data-testid="bookings-time-filter-summary">
                {activeCount > 0 ? summary : 'No filter active'}
              </div>
              <div className="flex gap-1.5">
                <Button variant="outline" size="sm" onClick={reset} data-testid="bookings-time-filter-reset">
                  Reset
                </Button>
                <Button size="sm" onClick={() => setOpen(false)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="bookings-time-filter-apply">
                  Done
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {activeCount > 0 && summary && (
        <div className="absolute left-0 right-0 -bottom-5 text-[10px] text-emerald-700 truncate pointer-events-none">
          {summary}
        </div>
      )}
    </div>
  );
}
